import { Link } from "react-router-dom";
import { api } from "../api/client";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { LoadingRows, ResourceError } from "../components/ResourceNotice";
import { TaskStatusCard } from "../components/TaskStatusCard";
import { useAuth } from "../context/AuthContext";
import { useTaskFeed } from "../context/TaskFeedContext";
import { useRemoteResource } from "../hooks/useRemoteResource";
import { shortNumber } from "../utils/format";

export function OverviewPage() {
  const { user } = useAuth();
  const taskFeed = useTaskFeed();
  const accountLists = useRemoteResource(api.accountLists.list);
  const accountCount = accountLists.data?.items.reduce((sum, item) => sum + Number(item.account_count || 0), 0) || 0;
  const finished = taskFeed.tasks.filter((task) => ["completed", "partial_failed", "failed"].includes(task.status));
  const success = finished.reduce((sum, task) => sum + task.success_count, 0);
  const attempted = finished.reduce((sum, task) => sum + task.success_count + task.failed_count, 0);
  const successRate = attempted ? Math.round((success / attempted) * 100) : 0;

  return (
    <div className="page-stack overview-page">
      <section className="overview-hero">
        <div className="overview-hero__copy">
          <span className="hero-signal"><i /> 系统正在同步</span>
          <h2>{taskFeed.activeTasks.length ? `有 ${taskFeed.activeTasks.length} 个任务正在推进` : `早上好，${user?.username}`}</h2>
          <p>{taskFeed.activeTasks.length ? "离开页面不会中断任务，完成后账号状态会自动回到账号库。" : "从账号预检开始一次清晰、可恢复的授权流程。"}</p>
          <div className="hero-actions">
            <Link className="button button--lime" to={taskFeed.activeTasks.length ? `/tasks/${taskFeed.activeTasks[0].id}` : "/accounts"}>
              {taskFeed.activeTasks.length ? "查看运行现场" : "准备账号"}<Icon name="arrow" size={18} />
            </Link>
            <Link className="button button--hero-quiet" to="/tasks">查看任务历史</Link>
          </div>
        </div>
        <div className="overview-hero__dial" aria-label={`${taskFeed.activeTasks.length} 个活动任务`}>
          <span className="dial-ring"><i /></span>
          <div><b>{taskFeed.activeTasks.length}</b><span>ACTIVE</span></div>
        </div>
      </section>

      <section className="metric-ribbon" aria-label="工作空间指标">
        <div><span>可用积分</span><b>{shortNumber(user?.credit_balance || 0)}</b><small>成功授权后扣除</small></div>
        <div><span>服务端账号</span><b>{accountLists.unsupported ? "—" : shortNumber(accountCount)}</b><small>{accountLists.unsupported ? "当前环境未接入" : `${accountLists.data?.items.length || 0} 个列表`}</small></div>
        <div><span>历史成功率</span><b>{attempted ? `${successRate}%` : "—"}</b><small>{attempted ? `基于 ${attempted} 个结果` : "等待首个任务"}</small></div>
        <div className="metric-ribbon__signal"><Icon name="pulse" size={24} /><span><b>自动恢复已开启</b><small>活动任务每 3 秒同步</small></span></div>
      </section>

      <div className="overview-grid">
        <section className="content-zone content-zone--tasks">
          <div className="section-heading">
            <div><span className="eyebrow">正在发生</span><h2>运行现场</h2></div>
            <button className="icon-button" onClick={taskFeed.refresh} aria-label="刷新任务" title="刷新任务"><Icon name="refresh" size={19} /></button>
          </div>
          {taskFeed.loading ? <LoadingRows count={4} /> : null}
          {taskFeed.error ? <ResourceError message={taskFeed.error} onRetry={taskFeed.refresh} /> : null}
          {!taskFeed.loading && !taskFeed.error && taskFeed.activeTasks.length ? <TaskStatusCard task={taskFeed.activeTasks[0]} featured /> : null}
          {!taskFeed.loading && !taskFeed.error && !taskFeed.activeTasks.length ? (
            <EmptyState compact icon="check" title="当前没有运行中的任务" description="任务列表已恢复完成。可以准备账号后发起下一次授权。" action={<Link className="text-link" to="/accounts">前往账号库 <Icon name="arrow" size={16} /></Link>} />
          ) : null}
        </section>

        <aside className="content-zone content-zone--route">
          <div className="section-heading"><div><span className="eyebrow">推荐路径</span><h2>下一步</h2></div></div>
          <ol className="route-list">
            <li className={accountCount ? "is-done" : "is-current"}><span>1</span><div><b>准备账号</b><small>{accountCount ? `${accountCount} 个账号可用` : "导入并检查字段"}</small></div></li>
            <li className={accountCount ? "is-current" : ""}><span>2</span><div><b>选择运行策略</b><small>Sub2、代理与并发</small></div></li>
            <li><span>3</span><div><b>确认并执行</b><small>核对影响和预计扣分</small></div></li>
          </ol>
          <Link to="/integrations" className="route-link">检查集成状态 <Icon name="arrow" size={16} /></Link>
        </aside>
      </div>

      {taskFeed.tasks.length ? (
        <section className="recent-strip">
          <div><span className="eyebrow">最近完成</span><h2>任务历史</h2></div>
          <div className="recent-strip__items">
            {taskFeed.tasks.slice(0, 3).map((task) => (
              <Link key={task.id} to={`/tasks/${task.id}`}><span>#{task.id}</span><b>{task.success_count}/{task.total_count}</b><small>成功</small><Icon name="arrow" size={17} /></Link>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
